import React, {
  useRef, useState, useEffect, useCallback,
} from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

/** Horizontal, scrollbar-less row of status badges with hold-to-scroll
 * arrow buttons that appear only when the badges overflow the available
 * width. Mirrors the week-view day-toggle footer navigation
 * (WeekViewDayToggleFooter): press-and-hold an arrow to scroll, release
 * to stop. Used by the transposed pre-save / saved-edit tables to keep
 * the kebab-toggle status pills on one line without a scrollbar. */
const Wrapper = styled('div')`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 4px;
`;

const Track = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  scroll-behavior: smooth;
  /* Hide the scrollbar across engines — navigation is via the arrows. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArrowsWrapper = styled('div')`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
`;

/** `inactive` is a styling-only prop (emotion filters it from the DOM).
 * The buttons deliberately use aria-disabled instead of the disabled
 * attribute: a disabled element stops receiving mouse events entirely, so
 * when an arrow flipped to disabled mid-hold (scroll reached the end) the
 * held press fell through to the parent react-beautiful-dnd drag handle
 * and started a phantom drag. */
const ArrowButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: ${(props) => (props.inactive ? 'default' : 'pointer')};
  &:hover {
    background: ${(props) => (props.inactive ? 'transparent' : props.theme.colors.surfaceHoverStrong)};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
`;

const StatusBadgeScroller = ({ children }) => {
  const { colors } = useTheme();
  const trackRef = useRef(null);
  const intervalRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  /** Re-check when the badge set changes (toggles on/off) and on resize. */
  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow, children]);

  const stopScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startScroll = useCallback((direction) => {
    stopScroll();
    const step = direction === 'left' ? -6 : 6;
    intervalRef.current = setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      el.scrollLeft += step;
      checkOverflow();
    }, 16);
    /** Failsafe: end the hold on the next release anywhere in the window,
     * in case the button's own mouseup/touchend never fires (e.g. the
     * pointer was moved off the arrow before releasing). */
    window.addEventListener('mouseup', stopScroll, { once: true });
    window.addEventListener('touchend', stopScroll, { once: true });
  }, [stopScroll, checkOverflow]);

  /** One-shot nudge used by keyboard activation (the hold-to-scroll interval
   * has no keyboard equivalent). */
  const nudge = useCallback((direction) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft += direction === 'left' ? -48 : 48;
    checkOverflow();
  }, [checkOverflow]);

  const handleArrowKeyDown = (direction, enabled) => (e) => {
    if (enabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      nudge(direction);
    }
  };

  useEffect(() => () => stopScroll(), [stopScroll]);

  return (
    <Wrapper>
      <Track ref={trackRef} onScroll={checkOverflow}>
        {children}
      </Track>
      {(showLeft || showRight) && (
        /** stopPropagation at the wrapper too: if a press lands on (or falls
         * through from) an arrow in any state, it must never reach the parent
         * react-beautiful-dnd drag handle and start a phantom drag. */
        <ArrowsWrapper
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <ArrowButton
            type='button'
            inactive={!showLeft}
            aria-disabled={!showLeft}
            aria-label='Scroll status badges left'
            onMouseDown={(e) => { e.stopPropagation(); if (showLeft) startScroll('left'); }}
            onMouseUp={stopScroll}
            onMouseLeave={stopScroll}
            onTouchStart={(e) => { e.stopPropagation(); if (showLeft) startScroll('left'); }}
            onTouchEnd={stopScroll}
            onKeyDown={handleArrowKeyDown('left', showLeft)}
          >
            <IcomoonReact iconSet={iconSet} size={10} icon='left-arrow' color={showLeft ? colors.text : colors.textTertiary} />
          </ArrowButton>
          <ArrowButton
            type='button'
            inactive={!showRight}
            aria-disabled={!showRight}
            aria-label='Scroll status badges right'
            onMouseDown={(e) => { e.stopPropagation(); if (showRight) startScroll('right'); }}
            onMouseUp={stopScroll}
            onMouseLeave={stopScroll}
            onTouchStart={(e) => { e.stopPropagation(); if (showRight) startScroll('right'); }}
            onTouchEnd={stopScroll}
            onKeyDown={handleArrowKeyDown('right', showRight)}
          >
            <IcomoonReact iconSet={iconSet} size={10} icon='right-arrow' color={showRight ? colors.text : colors.textTertiary} />
          </ArrowButton>
        </ArrowsWrapper>
      )}
    </Wrapper>
  );
};

StatusBadgeScroller.propTypes = {
  children: PropTypes.node,
};

StatusBadgeScroller.defaultProps = {
  children: null,
};

export default StatusBadgeScroller;
